import type { MtbTrailMetadata } from "~/lib/api";
import { cn } from "~/lib/utils";
import { MtbTrailCategoryBadge } from "./MtbTrailCategorySelector";
import { MtbTrailDifficultyBadge } from "./MtbTrailDifficultySelector";
import { DistanceDisplay } from "../DistanceEditor";
import { ElevationDisplay } from "../ElevationEditor";
import { StatsLine } from "../StatsLine";
import { MtbTrailStylePills } from "./MtbTrailStylePills";

interface AriaLabelProps {
  difficulty?: string;
  category?: string;
  distance?: { value: number; unit: string };
  elevation?: { gain: number; loss: number; unit: string };
  hasElevation: boolean;
}

function buildAriaLabel({
  difficulty,
  category,
  distance,
  elevation,
  hasElevation,
}: AriaLabelProps): string | undefined {
  const parts: string[] = [];

  if (difficulty) {
    parts.push(`${difficulty} difficulty`);
  }

  if (category) {
    parts.push(category);
  }

  if (distance) {
    const unit = distance.unit === "km" ? "kilometers" : "miles";
    parts.push(`${distance.value} ${unit}`);
  }

  if (hasElevation && elevation) {
    const unit = elevation.unit === "m" ? "meters" : "feet";
    if (elevation.gain > 0 && elevation.loss > 0) {
      if (elevation.gain === elevation.loss) {
        parts.push(`${elevation.gain} ${unit} elevation gain and loss`);
      } else {
        parts.push(`${elevation.gain} ${unit} gain, ${elevation.loss} ${unit} loss`);
      }
    } else if (elevation.gain > 0) {
      parts.push(`${elevation.gain} ${unit} elevation gain`);
    } else if (elevation.loss > 0) {
      parts.push(`${elevation.loss} ${unit} elevation loss`);
    }
  }

  if (parts.length === 0) return undefined;
  return `Trail stats: ${parts.join(", ")}`;
}

interface MtbTrailStatsProps {
  metadata: MtbTrailMetadata | null | undefined;
  compact?: boolean;
  outline?: boolean;
  className?: string;
}

export function MtbTrailStats({ metadata, compact, outline, className }: MtbTrailStatsProps) {
  if (!metadata) return null;

  const { difficulty, category, distance, elevation, character, features, build } = metadata;

  const hasElevation = !!(elevation && (elevation.gain > 0 || elevation.loss > 0));
  const hasCharacter = character && character.length > 0;
  const hasFeatures = features && features.length > 0;
  const hasStyle = !!(hasCharacter || hasFeatures || build);
  const hasStats = !!(difficulty || category || distance || hasElevation || hasStyle);

  if (!hasStats) return null;

  const ariaLabel = buildAriaLabel({ difficulty, category, distance, elevation, hasElevation });

  const coreStats = (
    <>
      {difficulty && <MtbTrailDifficultyBadge difficulty={difficulty} outline={outline} />}
      {category && <MtbTrailCategoryBadge category={category} />}
      {distance && <DistanceDisplay distance={distance} />}
      {hasElevation && <ElevationDisplay elevation={elevation} />}
    </>
  );

  if (compact) {
    return (
      <StatsLine className={className} aria-label={ariaLabel}>
        {coreStats}
      </StatsLine>
    );
  }

  return (
    <div className={cn("flex flex-col gap-2", className)} aria-label={ariaLabel}>
      <StatsLine>{coreStats}</StatsLine>
      {hasStyle && <MtbTrailStylePills metadata={metadata} />}
    </div>
  );
}
